// Constants

import { authorizedRepresentativeTypes } from './authorizedRepresentativeTypes.ts';
import { BLOOD_RELATIONSHIP_CODES } from './bloodRelationshipCodesList';
import { EXPENSE_TYPES, INCOME_TYPES, OTHER_INCOME_TYPES } from './incomeAndExpenceTypes';
import { FREQUENCY_TYPES } from './frequencyTypes';
import { GENDER_TYPES } from '../../../constants/ssap/miscellaneous/miscellaneous';
import { reconcileAptcTypes } from './reconcileAptcTypes';
import STATES_LIST from './statesList';
import states from '../../../constants/configs/states';

// Types

import { ContactMethod, IncomeDifferenceReason, MemberRemovalReason } from '../../../components/ssap/SsapTypes';

export const RENEWALS_REJECTION = '0'; // Unfortunately, due to limitations driven by Back-End numbers of years for renewals are not refactored to be Numbers
export const RENEWALS_YEARS_MAXIMUM = '5';

const OPTIONS_CONFIGS = {
    hideInput: {
        asComponent: null,
        isInputShown: false,
        isOnlyLabelShown: false,
    },
    showInput: {
        asComponent: null,
        isInputShown: true,
        isOnlyLabelShown: false,
    },
    showOnlyLabel: {
        asComponent: null,
        isInputShown: false,
        isOnlyLabelShown: true,
    },
    showOnlyLabelAsComponent: {
        asComponent: 'AlertInfo',
        isInputShown: false,
        isOnlyLabelShown: true
    }
};

/****************************************************

 Options for dropdowns in alphabetic order

 ****************************************************/

const AGREE_DISAGREE = [
    {
        value: true,
        text: 'ssap.input.option.i_agree',
    },
    {
        value: false,
        text: 'ssap.input.option.i_disagree',
    },
];

const AUTHORIZED_REPRESENTATIVES = [
    {
        value: authorizedRepresentativeTypes.SELF,
        text: 'ssap.input.option.any_helps.self',
    },
    {
        value: authorizedRepresentativeTypes.PROFESSIONAL_ASSISTANT,
        text: 'ssap.input.option.any_helps.professional_assistant',
    },
    {
        value: ({ isBrokerSelected }) => (isBrokerSelected ? OPTIONS_CONFIGS.showOnlyLabel : {}),
        text: 'ssap.input.option.any_helps.broker_link',
    },
    {
        value: authorizedRepresentativeTypes.NON_PROFESSIONAL_ASSISTANT,
        text: 'ssap.input.option.any_helps.non_professional_assistant',
    },
];

export const AUTHORIZED_REPRESENTATIVES_LIST = Object.assign(
    {},
    ...AUTHORIZED_REPRESENTATIVES.map(({ value, text }) => ({
        [value]: text,
    })),
);

const BLOOD_RELATIONSHIP = [
    {
        value: 'default',
        text: 'Relationship',
    },
    {
        value: BLOOD_RELATIONSHIP_CODES.SPOUSE,
        text: 'Spouse',
    },
    {
        value: BLOOD_RELATIONSHIP_CODES.PARENT_OF_CHILD,
        text: 'Parent of child',
    },
    {
        value: BLOOD_RELATIONSHIP_CODES.PARENT_OF_ADOPTED_CHILD,
        text: 'Parent of adopted child',
    },
    {
        value: BLOOD_RELATIONSHIP_CODES.ADOPTED_CHILD,
        text: 'Adopted child (son or daughter)',
    },
    {
        value: BLOOD_RELATIONSHIP_CODES.WARD_OF_COURT_APPOINTED_GUARDIAN,
        text: 'Ward of court-appointed guardian',
    },
    {
        value: BLOOD_RELATIONSHIP_CODES.STEPPARENT,
        text: 'Stepparent (stepfather or stepmother)',
    },
    {
        value: BLOOD_RELATIONSHIP_CODES.STEPCHILD,
        text: 'Stepchild (stepson or step daughter)',
    },
    {
        value: BLOOD_RELATIONSHIP_CODES.CHILD,
        text: 'Child (son or daughter)',
    },
    {
        value: BLOOD_RELATIONSHIP_CODES.COURT_APPOINTED_GUARDIAN,
        text: 'Court-appointed guardian',
    },
];

const COMMUNICATION_METHOD = [
    {
        value: ContactMethod.EMAIL,
        text: 'ssap.input.option.contact_method_email',
    },
    {
        value: ContactMethod.MAIL,
        text: 'ssap.input.option.contact_method_postal_mail',
    },
    {
        value: ContactMethod.NONE,
        text: 'ssap.input.option.none',
    },
];

export const COMMUNICATION_METHODS_LIST = Object.assign(
    {},
    ...COMMUNICATION_METHOD.map(({ value, text }) => ({
        [value]: text,
    })),
);

export const DEFAULT_COMMUNICATION_METHOD = 'Email';

const COUNTIES_FOR_EXACT_ZIPCODE = counties => [
    {
        value: 'default',
        text: 'ssap.input.option.county_default',
    },
    ...counties.map(county => {
        const [countyCode, countyName] = Object.entries(county)[0];
        return {
            value: countyCode,
            text: countyName,
        };
    }),
];

const COUNTRIES = countries => [
    {
        value: 'default',
        text: 'ssap.input.option.country',
    },
    ...countries.map(country => ({
        value: country.countryCode,
        text: country.countryName,
    })),
];

const COVERAGE = [
    {
        value: 'houseHoldContactOnly',
        text: 'ssap.input.option.primary_contact_only',
    },
    {
        value: 'otherFamilyMember',
        text: 'ssap.input.option.all_members',
    },
    {
        value: 'otherExcludingHousehold',
        text: 'ssap.input.option.all_members_except_primary_contact',
    },
];

const DEDUCTION_SOURCES = [
    {
        value: 'default',
        text: 'ssap.input.option.deduction_source',
    },
    {
        value: EXPENSE_TYPES.ALIMONY,
        text: 'ssap.input.option.alimony_paid',
    },
    {
        value: EXPENSE_TYPES.STUDENT_LOAN_INTEREST,
        text: 'ssap.input.option.student_loan_interest',
    },
    {
        value: EXPENSE_TYPES.BUSINESS,
        text: 'ssap.input.option.business_deduction',
    },
    {
        value: EXPENSE_TYPES.HEALTH_SAVING_ACCOUNT,
        text: 'ssap.input.option.hsa_deduction',
    },
    {
        value: EXPENSE_TYPES.SELF_EMPLOYMENT_TAX,
        text: 'ssap.input.option.self_employment_tax_deduction',
    },
    {
        value: EXPENSE_TYPES.SELF_EMPLOYED_PLANS,
        text: 'ssap.input.option.self_employed_plans_deduction',
    },
    {
        value: EXPENSE_TYPES.SELF_EMPLOYED_HEALTH_INSURANCE,
        text: 'ssap.input.option.self_employed_health_insurance_deduction',
    },
    {
        value: EXPENSE_TYPES.EARLY_WITHDRAWAL_PENALTIES,
        text: 'ssap.input.option.early_withdrawal_penalties_deduction',
    },
    {
        value: EXPENSE_TYPES.IRA,
        text: 'ssap.input.option.ira_deduction',
    },
    {
        value: EXPENSE_TYPES.MOVING,
        text: 'ssap.input.option.moving_deduction',
    },
    {
        value: EXPENSE_TYPES.EDUCATOR,
        text: 'ssap.input.option.educator_deduction',
    },
    {
        value: EXPENSE_TYPES.OTHER,
        text: 'ssap.input.option.other_deductions',
    },
];

const DELIVERY_OPTIONS_1095 = [
    {
        value: true,
        text: 'ssap.input.option.delivery_paperless',
    },
    {
        value: false,
        text: 'ssap.input.option.delivery_postal_mail',
    },
];

const DELIVERY_OPTIONS_NOTICES = [
    {
        value: ContactMethod.EMAIL,
        text: 'ssap.input.option.delivery_paperless',
    },
    {
        value: ContactMethod.MAIL,
        text: 'ssap.input.option.delivery_postal_mail',
    },
];

const DELIVERY_WAY = [
    {
        value: true,
        text: 'ssap.input.option.go_paperless',
    },
    {
        value: false,
        text: 'ssap.input.option.postal_mail_for_delivery',
    },
];

const GENDER = [
    {
        value: GENDER_TYPES.MALE,
        text: 'ssap.input.option.male',
    },
    {
        value: GENDER_TYPES.FEMALE,
        text: 'ssap.input.option.female',
    },
];

const INCOME_SOURCES = [
    {
        value: 'default',
        text: 'ssap.input.option.income_source',
    },
    {
        value: INCOME_TYPES.ALIMONY,
        text: 'ssap.input.option.alimony',
    },
    {
        value: INCOME_TYPES.CAPITAL_GAIN,
        text: 'ssap.input.option.capital_gains',
    },
    {
        value: INCOME_TYPES.FARMING_FISHING,
        text: 'ssap.input.option.farming_fishing',
    },
    {
        value: INCOME_TYPES.INVESTMENT,
        text: 'ssap.input.option.investment',
    },
    {
        value: INCOME_TYPES.JOB,
        text: 'ssap.input.option.job',
    },
    {
        value: INCOME_TYPES.OTHER,
        text: 'ssap.input.option.other_income',
    },
    {
        value: INCOME_TYPES.PENSION,
        text: 'ssap.input.option.pension',
    },
    {
        value: INCOME_TYPES.RENTAL_ROYALTY,
        text: 'ssap.input.option.rental_royalty',
    },
    {
        value: INCOME_TYPES.RETIREMENT,
        text: 'ssap.input.option.retirement',
    },
    {
        value: INCOME_TYPES.SCHOLARSHIP,
        text: 'ssap.input.option.scholarship',
    },
    {
        value: INCOME_TYPES.SELF_EMPLOYMENT,
        text: 'ssap.input.option.self_employment',
    },
    {
        value: INCOME_TYPES.SOCIAL_SECURITY_DISABILITY,
        text: 'ssap.input.option.ss_benefits',
    },
    {
        value: INCOME_TYPES.UNEMPLOYMENT,
        text: 'ssap.input.option.unemployment',
    },
];

export const INCOME_SOURCES_LIST = INCOME_SOURCES.reduce((obj, { value, text }) => {
    return {
        ...obj,
        [value]: text,
    };
}, {});

export const INCOME_SOURCES_TRIBAL_INCOME = [
    {
        value: INCOME_TYPES.AI_AN,
        text: 'ssap.input.option.ai_an_income',
    },
];

export const OTHER_INCOME_SUBTYPES = [
    {
        value: 'default',
        text: 'ssap.input.option.income_type',
    },
    {
        value: OTHER_INCOME_TYPES.CANCELED_DEBT,
        text: 'ssap.input.option.canceled_debts',
    },
    {
        value: OTHER_INCOME_TYPES.CASH_SUPPORT,
        text: 'ssap.input.option.cash_support',
    },
    {
        value: OTHER_INCOME_TYPES.COURT_AWARD,
        text: 'ssap.input.option.court_awards',
    },
    {
        value: OTHER_INCOME_TYPES.GAMBLING_PRIZE_AWARD,
        text: 'ssap.input.option.gambling',
    },
    {
        value: OTHER_INCOME_TYPES.JURY_DUTY_PAY,
        text: 'ssap.input.option.jury_duty_pay',
    },
    {
        value: OTHER_INCOME_TYPES.OTHER_INCOME,
        text: 'ssap.input.option.other',
    },
];

export const FLAGS_OPTIONS = [
    {
        value: 'default',
        text: 'Flag',
    },
    {
        value: true,
        text: 'True',
    },
    {
        value: null,
        text: 'Null',
    },
    {
        value: false,
        text: 'false',
    },
];

const FLOW_TYPE = [
    {
        value: true,
        text: 'ssap.input.option.financial_application',
    },
    {
        value: false,
        text: 'ssap.input.option.non_financial_application',
    },
];

const FREQUENCY = [
    {
        value: 'default',
        text: 'ssap.input.option.frequency',
    },
    {
        value: FREQUENCY_TYPES.HOURLY,
        text: 'ssap.input.option.hourly',
    },
    {
        value: FREQUENCY_TYPES.DAILY,
        text: 'ssap.input.option.daily',
    },
    {
        value: FREQUENCY_TYPES.WEEKLY,
        text: 'ssap.input.option.weekly',
    },
    {
        value: FREQUENCY_TYPES.BIWEEKLY,
        text: 'ssap.input.option.biweekly',
    },
    {
        value: FREQUENCY_TYPES.BIMONTHLY,
        text: 'ssap.input.option.bimonthly',
    },
    {
        value: FREQUENCY_TYPES.MONTHLY,
        text: 'ssap.input.option.monthly',
    },
    {
        value: FREQUENCY_TYPES.QUARTERLY,
        text: 'ssap.input.option.quarterly',
    },
    {
        value: FREQUENCY_TYPES.YEARLY,
        text: 'ssap.input.option.yearly',
    },
    {
        value: FREQUENCY_TYPES.ONCE,
        text: 'ssap.input.option.once',
    },
];

const FREQUENCY_FOR_EMPLOYER_SPONSORED_COVERAGE = FREQUENCY.filter(
    ({ value }) => value !== FREQUENCY_TYPES.HOURLY && value !== FREQUENCY_TYPES.DAILY,
);

export const FREQUENCY_LIST = Object.assign(
    {},
    ...FREQUENCY.map(({ value, text }) => ({
        [value]: text,
    })),
);

const INCOME_INDIVIDUAL_DISCREPANCIES = [
    {
        value: IncomeDifferenceReason.DECREASE_HOURS,
        text: 'ssap.input.option.decrease_hours',
    },
    {
        value: IncomeDifferenceReason.WAGE_CUT,
        text: 'ssap.input.option.wage_cut',
    },
    {
        value: IncomeDifferenceReason.STOPPED_WORKING,
        text: 'ssap.input.option.stopped_working',
    },
    {
        value: IncomeDifferenceReason.OTHER,
        text: 'ssap.input.option.other_reason',
    },
    {
        value: IncomeDifferenceReason.ENTERED_CORRECTLY,
        text: 'ssap.input.option.entered_correctly',
    },
];

const INCOME_TAX_HOUSEHOLD_DISCREPANCIES = [
    {
        value: IncomeDifferenceReason.HOUSEHOLD_MEMBER_CHANGED,
        text: 'ssap.input.option.household_member_changed',
    },
    {
        value: IncomeDifferenceReason.INCOME_LOSS,
        text: 'ssap.input.option.income_loss',
    },
    {
        value: IncomeDifferenceReason.SELF_EMPLOYMENT_INCOME_FLUCTUATION,
        text: 'ssap.input.option.self_employment_income_fluctuation',
    },
    {
        value: IncomeDifferenceReason.JOB_CHANGE,
        text: 'ssap.input.option.job_change',
    },
    {
        value: IncomeDifferenceReason.OTHER,
        text: 'ssap.input.option.other_reason',
    },
    {
        value: IncomeDifferenceReason.ENTERED_CORRECTLY,
        text: 'ssap.input.option.entered_correctly',
    },
];

const LANGUAGE = [
    {
        value: 'default',
        text: 'ssap.input.option.language_default',
    },
    {
        value: 'English',
        text: 'ssap.input.option.language_english',
    },
    {
        value: 'Spanish',
        text: 'ssap.input.option.language_spanish',
    },
];

export const LANGUAGE_LIST = LANGUAGE.reduce((obj, { value, text }) => {
    return {
        ...obj,
        [value]: text,
    };
}, {});

const NUMBER_OF_YEARS_NON_FINANCIAL_FLOW = [
    {
        value: RENEWALS_YEARS_MAXIMUM,
        text: 'ssap.input.option.five_years_non_financial_app',
    },
    {
        value: '4',
        text: 'ssap.input.option.four_years',
    },
    {
        value: '3',
        text: 'ssap.input.option.three_years',
    },
    {
        value: '2',
        text: 'ssap.input.option.two_years',
    },
    {
        value: '1',
        text: 'ssap.input.option.one_year',
    },
    {
        value: RENEWALS_REJECTION,
        text: 'ssap.input.option.do_not_renew_non_financial_app',
    },
];

const NUMBER_OF_YEARS_FINANCIAL_FLOW = [
    {
        value: RENEWALS_YEARS_MAXIMUM,
        text: 'ssap.input.option.five_years',
    },
    {
        value: '4',
        text: 'ssap.input.option.four_years',
    },
    {
        value: '3',
        text: 'ssap.input.option.three_years',
    },
    {
        value: '2',
        text: 'ssap.input.option.two_years',
    },
    {
        value: '1',
        text: 'ssap.input.option.one_year',
    },
    {
        value: RENEWALS_REJECTION,
        text: 'ssap.input.option.do_not_renew',
    },
];

const QUALIFYING_EVENT = [
    {
        value: 'default',
        text: 'ssap.input.option.event',
    },
    {
        value: 'CHANGE_IN_LEGAL_PRESENCE',
        text: 'ssap.input.option.change_in_immigration_status',
    },
    {
        value: 'CHANGE_IN_INCARCERATION',
        text: 'ssap.input.option.change_in_incarceration_status',
    },
    {
        value: 'CHANGE_IN_INCOME',
        text: 'ssap.input.option.change_in_income',
    },
    {
        value: 'BIRTH_ADOPTION',
        text: 'ssap.input.option.change_in_dependents',
    },
    {
        value: 'MOVED_INTO_STATE',
        text: 'ssap.input.option.change_of_address',
    },
    {
        value: 'DEATH',
        text: 'ssap.input.option.death',
    },
    {
        value: 'DIVORCE',
        text: 'ssap.input.option.divorce',
    },
    {
        value: 'LOST_OTHER_MIN_ESSENTIAL_COVERAGE',
        text: 'ssap.input.option.loss_of_coverage',
    },
    {
        value: 'MARRIAGE',
        text: 'ssap.input.option.marriage',
    },
    {
        value: 'CHANGE_IN_AMERICAN INDIAN / ALSAKA NATIVE STATUS',
        text: 'ssap.input.option.native_americans',
    },
];

const PROFIT_LOSS = [
    {
        value: 'default',
        text: 'ssap.input.option.select',
    },
    {
        value: true,
        text: 'ssap.input.option.profit',
    },
    {
        value: false,
        text: 'ssap.input.option.loss',
    },
];

export const RECONCILE_OPTIONS = [
    {
        value: reconcileAptcTypes.RECONCILED,
        text: 'ssap.input.option.reconciled',
    },
    {
        value: reconcileAptcTypes.DID_NOT_RECONCILE,
        text: 'ssap.input.option.didNotReconcile',
    },
    {
        value: reconcileAptcTypes.NEVER_RECONCILED,
        text: 'ssap.input.option.neverReconciled',
    },
];

const REASONABLE_EXPLANATIONS_FOR_NO_SSN = [
    {
        value: 'default',
        text: 'ssap.input.option.explanation',
    },
    {
        value: 'RELIGIOUS_EXCEPTION',
        text: 'ssap.input.option.religious_exception',
    },
    {
        value: 'JUST_APPLIED',
        text: 'ssap.input.option.just_applied',
    },
    {
        value: 'ILLNESS_EXCEPTION',
        text: 'ssap.input.option.illness_exception',
    },
    {
        value: 'CITIZEN_EXCEPTION',
        text: 'ssap.input.option.citizen_exception',
    },
];

export const REMOVE_MEMBER_ACTIONS = [
    {
        value: true,
        text: 'ssap.input.option.remove_applying_for_coverage',
    },
    {
        value: ({ isGoingToNotSeekingForCoverage }) =>
            isGoingToNotSeekingForCoverage ? OPTIONS_CONFIGS.showOnlyLabel : {},
        text: 'ssap.input.option.remove_applying_for_coverage.help_text',
    },
    {
        value: false,
        text: 'ssap.input.option.remove_member_from_application',
    },
];

export const REMOVE_MEMBER_REASONS = [
    {
        value: ({ isDeathOptionShown }) => {
            return isDeathOptionShown
                ? {
                    ...OPTIONS_CONFIGS.showInput,
                    optionValue: MemberRemovalReason.DEATH,
                }
                : OPTIONS_CONFIGS.hideInput;
        },
        text: 'ssap.input.option.removal_reason.death',
    },
    {
        value: ({ isDeathDateShown }) => (isDeathDateShown ? OPTIONS_CONFIGS.showOnlyLabel : {}),
        text: 'ssap.input.option.removal_reason.death_date',
    },
    {
        value: ({ isDivorceOptionDisabled }) => {
            return {
                ...OPTIONS_CONFIGS.showInput,
                isInputDisabled: isDivorceOptionDisabled,
                optionValue: MemberRemovalReason.DIVORCE,
            };
        },
        text: 'ssap.input.option.removal_reason.divorce',
    },
    {
        value: ({ isDivorceDateShown }) => (isDivorceDateShown ? OPTIONS_CONFIGS.showOnlyLabel : {}),
        text: 'ssap.input.option.removal_reason.divorce_date',
    },
    {
        value: ({ isOtherOptionDisabled }) => {
            return {
                ...OPTIONS_CONFIGS.showInput,
                isInputDisabled: isOtherOptionDisabled,
                optionValue: MemberRemovalReason.OTHER,
            };
        },
        text: 'ssap.input.option.removal_reason.other',
    },
];

export const DECEASED_MEMBER_ACTIONS = [
    {
        value: true,
        text: 'ssap.input.option.keep_deceased_member',
    },
    {
        value: false,
        text: 'ssap.input.option.remove_deceased_member',
    },
];

export const REASONABLE_EXPLANATIONS_LIST = Object.assign(
    {},
    ...REASONABLE_EXPLANATIONS_FOR_NO_SSN.map(({ value, text }) => ({
        [value]: text,
    })),
);

const SIGNATURE = [
    {
        value: false,
        text:
            'I am not able to complete an electronic signature. ' +
            '(If you select this option, you will need to upload a document to prove ' +
            'that there is a legal reason why you cannot sign.)\n',
    },
    {
        value: true,
        text: 'Signature',
    },
];

const STATE = [
    // Check if no usages and delete
    ...STATES_LIST.map(state => ({
        text: state.stateName,
        value: state.stateCode,
    })),
];

const STATES_FOR_TRIBES = tribesData => {
    return [
        {
            value: 'default',
            text: 'ssap.input.option.state_default',
        },
        ...tribesData.map(state => ({
            text: state.name,
            value: state.code,
        })),
    ];
};

export const getOptionsForHardcodedState = stateCode => {
    const state = STATES_LIST.find(state => state.stateCode === stateCode);
    return [
        {
            text: state.stateName,
            value: state.stateCode,
        },
    ];
};

export const getDynamicDropdownNumberOption = (inputNumber, numberLimitForDropdown) => {
    const setLimit = inputNumber <= numberLimitForDropdown ? inputNumber : numberLimitForDropdown;
    const populatedList = Array(setLimit)
        .fill(0)
        .map((e, i) => ({ text: i + 1, value: i + 1 }));
    return [
        {
            value: 'default',
            text: 'ssap.input.option.age',
        },
        ...populatedList,
    ];
};

const SPOUSE_ON_APPLICATION = [
    {
        value: true,
        text: 'ssap.input.option.someone_on_application',
    },
    {
        value: false,
        text: 'ssap.input.option.not_applying_for_coverage',
    },
];

const SUFFIX = [
    {
        value: 'default',
        text: 'ssap.input.option.suffix_default',
    },
    {
        value: 'Jr.',
        text: 'ssap.input.option.suffix_junior',
    },
    {
        value: 'Sr.',
        text: 'ssap.input.option.suffix_senior',
    },
    {
        value: 'III',
        text: 'ssap.input.option.suffix_third',
    },
    {
        value: 'IV',
        text: 'ssap.input.option.suffix_forth',
    },
];

const TRIBES = (tribes, stateCode) => {
    const neededState = tribes.find(state => state.code === stateCode);
    return [
        {
            value: 'default',
            text: 'ssap.input.option.tribal_name',
        },
        ...(neededState
            ? neededState.tribes.map(tribe => ({
                value: tribe.tribeCode,
                text: tribe.tribeName,
            }))
            : []),
    ];
};

export const YES_NO = [
    {
        value: true,
        text: 'ssap.input.option.yes',
    },
    {
        value: false,
        text: 'ssap.input.option.no',
    },
];

export const YES_NO_TAX_CREDIT_PREFERENCE_FOR_PREGNANT = [
    {
        value: false,
        text: 'ssap.input.option.yes',
    },
    {
        value: () => OPTIONS_CONFIGS.showOnlyLabelAsComponent,
        text: 'ssap.input.option.text_credit_preference_yes_answer_explanation'
    },
    {
        value: true,
        text: 'ssap.input.option.no',
    },
    {
        value: () => OPTIONS_CONFIGS.showOnlyLabelAsComponent,
        text: 'ssap.input.option.text_credit_preference_no_answer_explanation'
    }
];

export const PREFERENCES_FOR_PREGNANT = stateCode => {
    const overrides = [
        {
            target: 'ssap.input.option.yes',
            replacement: 'ssap.input.option.exchange_coverage',
        },
        {
            target: 'ssap.input.option.no',
            replacement: 'ssap.input.option.medicaid_chip_coverage',
        }
    ];

    const stateWithOverriddenOptions = [
        states.IDAHO
    ];


    return YES_NO_TAX_CREDIT_PREFERENCE_FOR_PREGNANT.map(option => {
        const {
            value,
            text
        } = option;

        const override = overrides
            .find(o => o.target === text);

        if (stateWithOverriddenOptions.includes(stateCode) && override) {
            return {
                value,
                text: override.replacement,
            };
        } else {
            return option;
        }
    });
};

export const YES_NO_PREFER_NOT_TO_ANSWER = [
    {
        value: true,
        text: 'ssap.input.option.yes',
    },
    {
        value: false,
        text: 'ssap.input.option.no',
    },
    {
        value: null,
        text: 'ssap.input.option.prefer_not_to_answer',
    },
];

export const YES_NO_DISABLED = [
    {
        value: true,
        text: 'ssap.input.option.yes',
        disabled: true,
    },
    {
        value: false,
        text: 'ssap.input.option.no',
    },
];

export const FILING_JOINTLY_YES_NO = [
    {
        value: 'jointly',
        text: 'ssap.input.option.yes',
    },
    {
        value: 'separately',
        text: 'ssap.input.option.no',
    },
];

export const RIDP_VERIFICATION_DOCUMENT_OPTIONS = [
    {
        value: 'ridp.manualverification.uploadfile.option1',
        text: 'ridp.manualverification.uploadfile.option1',
    },
    {
        value: 'ridp.manualverification.uploadfile.option2',
        text: 'ridp.manualverification.uploadfile.option2',
    },
    {
        value: 'ridp.manualverification.uploadfile.option3',
        text: 'ridp.manualverification.uploadfile.option3',
    },
    {
        value: 'ridp.manualverification.uploadfile.option4',
        text: 'ridp.manualverification.uploadfile.option4',
    },
    {
        value: 'ridp.manualverification.uploadfile.option5',
        text: 'ridp.manualverification.uploadfile.option5',
    },
    {
        value: 'ridp.manualverification.uploadfile.option6',
        text: 'ridp.manualverification.uploadfile.option6',
    },
    {
        value: 'ridp.manualverification.uploadfile.option7',
        text: 'ridp.manualverification.uploadfile.option7',
    },
    {
        value: 'ridp.manualverification.uploadfile.option8',
        text: 'ridp.manualverification.uploadfile.option8',
    },
    {
        value: 'ridp.manualverification.uploadfile.option9',
        text: 'ridp.manualverification.uploadfile.option9',
    },
    {
        value: 'ridp.manualverification.uploadfile.option10',
        text: 'ridp.manualverification.uploadfile.option10',
    },
    {
        value: 'ridp.manualverification.uploadfile.option11',
        text: 'ridp.manualverification.uploadfile.option11',
    },
    {
        value: 'ridp.manualverification.uploadfile.option12',
        text: 'ridp.manualverification.uploadfile.option12',
    },
    {
        value: 'ridp.manualverification.uploadfile.option13',
        text: 'ridp.manualverification.uploadfile.option13',
    },
    {
        value: 'ridp.manualverification.uploadfile.option14',
        text: 'ridp.manualverification.uploadfile.option14',
    },
    {
        value: 'ridp.manualverification.uploadfile.option15',
        text: 'ridp.manualverification.uploadfile.option15',
    },
    {
        value: 'ridp.manualverification.uploadfile.option16',
        text: 'ridp.manualverification.uploadfile.option16',
    },
    {
        value: 'ridp.manualverification.uploadfile.option17',
        text: 'ridp.manualverification.uploadfile.option17',
    },
    {
        value: 'ridp.manualverification.uploadfile.option18',
        text: 'ridp.manualverification.uploadfile.option18',
    },
];

export const DROPDOWN_OPTIONS = {
    AGREE_DISAGREE,
    AUTHORIZED_REPRESENTATIVES,
    BLOOD_RELATIONSHIP,
    COMMUNICATION_METHOD,
    COUNTIES_FOR_EXACT_ZIPCODE,
    COUNTRIES,
    COVERAGE,
    DECEASED_MEMBER_ACTIONS,
    DEDUCTION_SOURCES,
    DELIVERY_OPTIONS_1095,
    DELIVERY_OPTIONS_NOTICES,
    DELIVERY_WAY,
    GENDER,
    INCOME_INDIVIDUAL_DISCREPANCIES,
    INCOME_TAX_HOUSEHOLD_DISCREPANCIES,
    INCOME_SOURCES,
    INCOME_SOURCES_TRIBAL_INCOME,
    FILING_JOINTLY_YES_NO,
    FLAGS_OPTIONS,
    FLOW_TYPE,
    FREQUENCY,
    FREQUENCY_FOR_EMPLOYER_SPONSORED_COVERAGE,
    LANGUAGE,
    NUMBER_OF_YEARS_FINANCIAL_FLOW,
    NUMBER_OF_YEARS_NON_FINANCIAL_FLOW,
    OTHER_INCOME_SUBTYPES,
    PREFERENCES_FOR_PREGNANT,
    PROFIT_LOSS,
    QUALIFYING_EVENT,
    REASONABLE_EXPLANATIONS_FOR_NO_SSN,
    RECONCILE_OPTIONS,
    REMOVE_MEMBER_ACTIONS,
    REMOVE_MEMBER_REASONS,
    SIGNATURE,
    SPOUSE_ON_APPLICATION,
    STATE,
    STATES_FOR_TRIBES,
    SUFFIX,
    TRIBES,
    YES_NO,
    YES_NO_PREFER_NOT_TO_ANSWER,
    YES_NO_TAX_CREDIT_PREFERENCE_FOR_PREGNANT,
    YES_NO_DISABLED,
    RIDP_VERIFICATION_DOCUMENT_OPTIONS,
};
